import * as React from "react";
import styled from "styled-components";
import { useMemo, useState } from "react";
import { useSpring, animated } from "react-spring";
import { Link } from "../Link";
import {
  toREM,
  toVWDesktop,
  toVWMobile,
} from "../../../helpers/styles";
import { BREAKPOINTS } from "../../../helpers/theme";
import { isUSLocale } from "../../../helpers/locales";

export enum ButtonType {
  PRIMARY_LIGHT = "PRIMARY_LIGHT",
  PRIMARY_DARK = "PRIMARY_DARK",
  SECONDARY_LIGHT = "SECONDARY_LIGHT",
  YELLOW = "YELLOW",
}

export type ButtonLinkProps = {
  label: string;
  link: string;
  type?: ButtonType;
  className?: string;
  iconComponent?: React.ReactElement;
  anchor?: boolean;
  blank?: boolean;
  disabled?: boolean;
};

export const ButtonLink = ({
  label,
  link,
  type = ButtonType.PRIMARY_LIGHT,
  className,
  iconComponent,
  ...props
}: ButtonLinkProps): JSX.Element => {
  const [active, setActive] = useState<boolean>(false);
  const reskin = isUSLocale();

  const bgColors = {
    primaryLight: !reskin ? ["#cbebbd", "#75c154"] : ["#dbefb8", "#b5dd71"],
    primaryDark: !reskin ? ["#3e3e3e", "#000"] : ["#234521", "#012604"],
    secondaryLight: !reskin ? ["#cbebbd", "#fff"] : ["#dbefb8", "#f6f1e7"],
    yellow: !reskin ? ["#ffd27d", "#fdc04e"] : ["#dbefb8", "#b5dd71"],
  };

  const hoverBackgroundAnimation = useMemo(() => {
    const transform = `scale(${active ? 0.95 : 1})`;
    let background = active ? bgColors.primaryLight[0] : bgColors.primaryLight[1];
    if (type === ButtonType.PRIMARY_DARK) {
      background = active ? bgColors.primaryDark[0] : bgColors.primaryDark[1];
    }
    if (type === ButtonType.SECONDARY_LIGHT) {
      background = active ? bgColors.secondaryLight[0] : bgColors.secondaryLight[1];
    }
    if (type === ButtonType.YELLOW) {
      background = active ? bgColors.yellow[0] : bgColors.yellow[1];
    }
    return {
      transform,
      background,
    };
  }, [active]);
  const backgroundStyles = useSpring(
    hoverBackgroundAnimation,
  );

  const Component = useMemo(() => {
    if (type === ButtonType.PRIMARY_DARK) {
      return PrimaryDark;
    }
    if (type === ButtonType.SECONDARY_LIGHT) {
      return SecondaryLight;
    }
    if (type === ButtonType.YELLOW) {
      return Yellow;
    }
    return PrimaryLight;
  }, [type]);

  return (
    <Component
      className={`ignore${className ? ` ${className}` : ""}`}
      to={link}
      {...props}
      onMouseEnter={
        !props.disabled
          ? () => {
              setActive(true);
              if (
                props.onMouseEnter &&
                typeof props.onMouseEnter === "function"
              ) {
                props.onMouseEnter();
              }
            }
          : () => null
      }
      onMouseLeave={
        !props.disabled
          ? () => {
              setActive(false);
              if (
                props.onMouseLeave &&
                typeof props.onMouseLeave === "function"
              ) {
                props.onMouseLeave();
              }
            }
          : () => null
      }
    >
      {!!iconComponent && iconComponent}
      <Label className="font-gt-pressura">{label}</Label>
      <Background style={backgroundStyles} />
    </Component>
  );
};

const PrimaryLight = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  border: none;
  color: var(--color-black);
  background-color: transparent;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  width: 100%;
  display: block;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(16)}rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  line-height: ${toREM(19)}rem;
  padding: ${toVWMobile(19)}vw;
  align-self: center;
  position: relative;

  &[disabled] {
    color: var(--color-black) !important;
    cursor: default;
    > div {
      background: var(--color-brown-30) !important;
    }
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: fit-content;
    min-width: ${toVWDesktop(180)}vw;
    font-size: ${toREM(16)}rem;
    letter-spacing: 0.01em;
    line-height: ${toREM(19)}rem;
    padding: ${toVWDesktop(19)}vw;
    transition: transform 0.5s;
  }
`;

const Background = styled(animated.div)`
  width: 100%;
  height: 100%;
  position: absolute;
  pointer-events: none;
  top: 0;
  left: 0;
  z-index: -1;
`;

const PrimaryDark = styled(PrimaryLight)`
  color: var(--color-white);
`;

const SecondaryLight = styled(PrimaryLight)`
  color: var(--color-black);
`;

const Yellow = styled(PrimaryLight)`
  color: var(--color-black);
`;

const Label = styled(animated.span)``;

export default ButtonLink;
